import { useState } from 'react';
import { X } from 'lucide-react';

interface EditAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  admin?: {
    id: string;
    name: string;
    email: string;
    phone: string;
    avatar: string;
    role: string;
    status: 'Active' | 'Inactive';
  };
}

const EditAdminModal = ({ isOpen, onClose, admin }: EditAdminModalProps) => {
  const [formData, setFormData] = useState({
    fullName: admin?.name || '',
    phone: admin?.phone || '',
    role: admin?.role || 'Super Admin',
    accessEnabled: admin?.status === 'Active',
  });

  if (!isOpen || !admin) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleToggle = () => {
    setFormData(prev => ({
      ...prev,
      accessEnabled: !prev.accessEnabled,
    }));
  };

  return (
    <>
      {isOpen && admin && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-md p-8 relative shadow-2xl my-8">
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 bg-transparent border-0 cursor-pointer"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Member Configuration Label */}
            <p className="text-xs uppercase font-bold text-gray-600 tracking-wider mb-2">Member Configuration</p>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Edit Admin Profile</h2>

            {/* Admin Avatar and Info */}
            <div className="flex items-center gap-4 mb-8 pb-6 border-b border-gray-200">
              <div className="w-16 h-16 rounded-full bg-gray-300 flex items-center justify-center text-3xl relative flex-shrink-0">
                {admin.avatar}
                <span className="absolute bottom-0 right-0 w-5 h-5 bg-teal-600 rounded-full border-2 border-white flex items-center justify-center text-white text-xs">✓</span>
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 truncate">{admin.name}</p>
                <p className="text-sm text-gray-600 truncate">{admin.email}</p>
              </div>
            </div>

            {/* Form */}
            <div className="space-y-5">
              {/* Full Name */}
              <div>
                <label className="block text-xs uppercase font-bold text-gray-700 tracking-wider mb-2">Full Name</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                />
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-xs uppercase font-bold text-gray-700 tracking-wider mb-2">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                />
              </div>

              {/* Administrative Role */}
              <div>
                <label className="block text-xs uppercase font-bold text-gray-700 tracking-wider mb-2">Administrative Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white appearance-none cursor-pointer transition-colors"
                >
                  <option>Super Admin</option>
                  <option>Operations Manager</option>
                  <option>Finance Admin</option>
                  <option>Compliance Reviewer</option>
                </select>
              </div>

              {/* Account Access Status */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Account Access Status</p>
                  <p className="text-xs text-gray-600 mt-1">Currently enabled for platform access</p>
                </div>
                <button
                  onClick={handleToggle}
                  className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors flex-shrink-0 ${
                    formData.accessEnabled ? 'bg-teal-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
                      formData.accessEnabled ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 mt-8 pt-4">
              <button
                onClick={() => {
                  // Handle save changes
                  onClose();
                }}
                className="flex-1 bg-teal-700 hover:bg-teal-800 text-white font-semibold py-3 rounded-lg transition-colors"
              >
                Save Changes
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-900 font-semibold py-3 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditAdminModal;
